import React, { useEffect, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import PageHeaderText from 'components/base/page-header-text';
import FormStepper from 'components/base/form-stepper';
import UpdateVendorStoreDetails from './update-vendor-store-details';
import useVendorContext from 'contexts/vendor.context';
import { apiService } from 'services/base.service';
import { useNavigate } from 'react-router-dom';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { DefaultVendorToEdit } from 'contexts/vendor.context/provider';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { VendorStoreItemType } from 'models/vendors.model';

const EditVendorStoreItems = () => {
  const { setIsBackLink, setBackLinkTitle, formStoreItems } = useDashboardContext();
  const { vendorToEdit, setVendorToEdit } = useVendorContext();
  const [storeItemsToEdit, setStoreItemsToEdit] = useState<VendorStoreItemType[]>([]);
  const [disabled, setDisabled] = useState(true);
  const [isUpdatingVendor, setIsUpdatingVendor] = useState(false);
  const [isFetchingStoreITems, setIsFetchingStoreItems] = useState(false);
  const [vendorIngredientIdsToDelete, setVendorIngredientIdsToDelete] = useState<string[]>([]);

  const router = useNavigate();

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to procurement');
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  function isStepEmpty(data: any): boolean {
    for (const item of data) {
      for (const key in item) {
        if (typeof item[key] === 'string' && item[key] === '') {
          return true;
        }
      }
    }
    return false;
  }

  const isDisabled = () => {
    if (storeItemsToEdit) {
      isStepEmpty(storeItemsToEdit) ? setDisabled(true) : setDisabled(false);
    } else {
      setDisabled(false);
    }
  };

  useEffect(() => {
    isDisabled();
  }, [storeItemsToEdit]);

  const fetchVendorStoreItems = async () => {
    try {
      setIsFetchingStoreItems(true);
      const res = await apiService.get(`vendors/ingredients?vendor_id=${vendorToEdit.id}`);
      if (res) {
        setStoreItemsToEdit(
          res?.data?.results.map((result: any) => {
            return {
              id: result?.id,
              ingredient_id: result?.ingredient_id,
              gram_price: (
                Number(result?.gram_price) * Number(result?.ingredient.unit_weight)
              ).toFixed(2),
              vendor_id: result?.vendord_id,
              ingredient: {
                name: result?.ingredient.name,
                unit_weight: result?.ingredient.unit_weight,
                unit_type: result?.ingredient?.unit_type || 'gram'
              }
            };
          })
        );
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response.data.message || 'Error fetching vendor store items'}
          type='error'
        />
      );
    } finally {
      setIsFetchingStoreItems(false);
    }
  };

  useEffect(() => {
    fetchVendorStoreItems();
  }, []);

  const selectedIngredient = (id: string) => {
    return id
      ? formStoreItems.filter((ingredient) => ingredient.id === id)[0]
      : { name: '', unit_type: '', unit_weight: '' };
  };

  const updateVendor = async () => {
    const data = {
      deletions: vendorIngredientIdsToDelete,
      updates: storeItemsToEdit
        .filter(
          (vendorIngredient) =>
            vendorIngredient.id && !vendorIngredientIdsToDelete.includes(vendorIngredient.id)
        )
        .map(({ gram_price, ingredient, id }) => ({
          id,
          gram_price: Number((Number(gram_price) / Number(ingredient.unit_weight)).toFixed(2))
        })),
      new_items: storeItemsToEdit
        .filter((vendorIngredient) => !vendorIngredient.id)
        .map(({ gram_price, ingredient_id }) => ({
          vendor_id: vendorToEdit.id,
          ingredient_id: ingredient_id,
          gram_price: Number(
            (Number(gram_price) / Number(selectedIngredient(ingredient_id).unit_weight)).toFixed(2)
          )
        }))
    };
    try {
      setIsUpdatingVendor(true);
      const res = await apiService.post(`vendor/ingredients/bulk-update-with-vendors`, { data });
      if (res) {
        router(MAIN_STORE_ROUTE_KEYS.VENDORS);
        setVendorToEdit(DefaultVendorToEdit);
        setStoreItemsToEdit([]);
        toast(<ToastMessage message='Vendor updated successfully' type='success' />);
      }
    } catch (err: any) {
      toast(
        <ToastMessage message={err.response.data.message || 'Error updating vendor'} type='error' />
      );
    } finally {
      setIsUpdatingVendor(false);
    }
  };

  return (
    <div>
      <PageHeaderText title={`Edit ${vendorToEdit.name}`} description="Update vendors' details" />
      <div className='w-[400px] md:w-[670px]'>
        <FormStepper
          title='Update vendor store items'
          hideSteps
          step={1}
          noOfSteps={1}
          setStep={() => {
            return;
          }}
          handleConfirmClick={updateVendor}
          disabled={disabled}
          loading={isUpdatingVendor}>
          <div>
            <UpdateVendorStoreDetails
              isFetchingStoreItems={isFetchingStoreITems}
              setVendorIngredientIdsToDelete={setVendorIngredientIdsToDelete}
              vendorIngredientIdsToDelete={vendorIngredientIdsToDelete}
              storeItemsToEdit={storeItemsToEdit}
              setStoreItemsToEdit={setStoreItemsToEdit}
            />
          </div>
        </FormStepper>
      </div>
    </div>
  );
};

export default EditVendorStoreItems;
